import { useState } from 'react';

import { Gate, useAuth } from '../../../global/contexts/auth/auth';
import { IconAnimals, IconClose, IconFarm, IconUsers } from '../../../common/icons';
import SectionTabs from '../../navigation/section-tabs';
import { AUDIT_TABS } from '../../navigation/tabs';
import AnimalSearch from '../../animals/forms/animal-search';
import ClientFarmSearch from '../../clients/forms/search';
import { useMergePreview, mergeConflicts } from '../contexts/merge';
import MergeConfirmModal from '../forms/merge-confirm-modal';

import '../../../common/styles/catalog.css';
import '../styles/audit.css';

// Auditoría · Merges (admin-merge.md · 0129): unificar dos registros duplicados de la
// misma entidad (animals/clients/farms). Base persiste, absorbida se apaga. Buscadores
// reutilizados; dry-run automático y gráfico; confirmación explícita (irreversible).
const ENTITIES = [
    { key: 'animals', label: 'Animales', domain: 'animals', noun: 'animales', icon: <IconAnimals /> },
    { key: 'clients', label: 'Clientes', domain: 'clients', noun: 'clientes', icon: <IconUsers /> },
    { key: 'farms',   label: 'Fincas',   domain: 'farms',   noun: 'fincas',   icon: <IconFarm /> },
];

const CONFLICT_HELP = {
    ownership_overflow:         'La propiedad combinada supera 100%. Ajustá los porcentajes antes de unificar.',
    double_active_offering:     'Ambos tienen una oferta de participación activa (máx. 1 por animal).',
    genealogy_cycle:            'La base es ancestro o descendiente de la absorbida (crearía un ciclo).',
    sg_candidate_reference:     'La absorbida figura como candidata en una reconciliación SG.',
    double_active_partnership:  'Ambos clientes tienen parcería vigente (máx. 1 por cliente).',
    recipient_number_collision: 'Ambas fincas tienen receptoras con el mismo número.',
};

const labelFor = (r) => (!r ? '' : r.name || r.number || r.code || `#${r.id}`);
function subFor(entity, r) {
    if (!r) return '';
    if (entity === 'animals') return [r.number, r.register, r.race?.name].filter(Boolean).join(' · ');
    if (entity === 'clients') return [r.document, r.email].filter(Boolean).join(' · ');
    return [r.owner?.name, r.location].filter(Boolean).join(' · ');
}

function EntitySearch({ entity, onSelect }) {
    if (entity === 'animals') return <AnimalSearch placeholder="Buscar animal por nombre, número o registro…" onSelect={onSelect} />;
    if (entity === 'clients') return <ClientFarmSearch scope="clients" placeholder="Buscar cliente por nombre o documento…" onSelect={(it) => onSelect(it)} />;
    return <ClientFarmSearch scope="all" farmsOnly placeholder="Buscar finca por nombre…" onSelect={(it) => onSelect(it)} />;
}

function Slot({ role, entity, record, onSelect, onClear }) {
    const isBase = role === 'base';
    return (
        <div className={`mrg-slot mrg-slot--${role}`}>
            <span className="mrg-slot__label">{isBase ? 'Registro base' : 'Registro a absorber'}</span>
            <span className="mrg-slot__hint">{isBase ? 'Persiste · recibe todas las referencias' : 'Se apaga (soft-delete)'}</span>
            {record ? (
                <div className="mrg-picked">
                    <div className="mrg-picked__body">
                        <span className="mrg-picked__name">{labelFor(record)}</span>
                        <span className="mrg-picked__sub">{subFor(entity, record) || `#${record.id}`}</span>
                    </div>
                    <button type="button" className="mrg-picked__clear" onClick={onClear} aria-label="Quitar selección"><IconClose /></button>
                </div>
            ) : (
                <EntitySearch entity={entity} onSelect={onSelect} />
            )}
        </div>
    );
}

export default function MergesPanel() {
    const { can } = useAuth();
    const allowed = ENTITIES.filter((e) => can(e.domain, 'admin'));
    const [entity, setEntity] = useState(() => allowed[0]?.key ?? ENTITIES[0].key);
    const [base, setBase] = useState(null);
    const [absorbed, setAbsorbed] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const cur = ENTITIES.find((e) => e.key === entity) ?? ENTITIES[0];
    const sameRecord = !!(base && absorbed && base.id === absorbed.id);
    const ready = !!base && !!absorbed && !sameRecord;

    const { data: preview, isFetching: previewing, error: previewError } = useMergePreview(entity, base?.id, absorbed?.id);
    const conflicts = preview?.conflicts?.length ? preview.conflicts : mergeConflicts(previewError);
    const hardError = !!previewError && conflicts.length === 0; // error real, no de conflictos
    const affectedRows = Object.entries(preview?.affected ?? {});
    const released = Object.entries(preview?.released_uniques ?? {});
    const affectedTotal = affectedRows.reduce((a, [, n]) => a + (Number(n) || 0), 0);
    const maxAffected = Math.max(1, ...affectedRows.map(([, n]) => Number(n) || 0));
    const canApply = ready && !!preview && conflicts.length === 0 && !previewing;

    const switchEntity = (key) => { if (key === entity) return; setEntity(key); setBase(null); setAbsorbed(null); };
    const reset = () => { setBase(null); setAbsorbed(null); setConfirming(false); };

    return (
        <div className="cat-overview mrg">
            <SectionTabs tabs={AUDIT_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Auditoría</h1>
                </div>
            </header>

            <p className="mrg__lede">
                Unificá dos registros duplicados en uno. Elegí el tipo, buscá el <strong>base</strong> (persiste) y el
                registro <strong>a absorber</strong> (se apaga), y previsualizá el impacto antes de confirmar.
            </p>

            <div className="mrg-seg" role="group" aria-label="Tipo de registro a unificar">
                {ENTITIES.map((e) => (
                    <Gate key={e.key} domain={e.domain} action="admin">
                        <button
                            type="button"
                            className={`mrg-seg__btn ${entity === e.key ? 'mrg-seg__btn--active' : ''}`}
                            aria-pressed={entity === e.key}
                            onClick={() => switchEntity(e.key)}
                        >
                            <span className="mrg-seg__icon" aria-hidden="true">{e.icon}</span>{e.label}
                        </button>
                    </Gate>
                ))}
            </div>

            <div className="mrg-pair">
                <Slot role="absorb" entity={entity} record={absorbed} onSelect={setAbsorbed} onClear={() => setAbsorbed(null)} />
                <div className="mrg-pair__arrow" aria-hidden="true"><span>absorbe</span><em>→</em></div>
                <Slot role="base" entity={entity} record={base} onSelect={setBase} onClear={() => setBase(null)} />
            </div>

            {sameRecord && <div className="mrg-note mrg-note--warn">Elegí dos registros distintos.</div>}

            {!ready ? (
                <div className="mrg-preview mrg-preview--empty">Elegí ambos registros para ver el impacto.</div>
            ) : previewing ? (
                <div className="mrg-preview mrg-preview--empty">Analizando impacto…</div>
            ) : conflicts.length > 0 ? (
                <div className="mrg-preview">
                    <div className="mrg-preview__head">
                        <span className="mrg-badge mrg-badge--block">{conflicts.length} conflicto(s) · bloqueado</span>
                    </div>
                    <ul className="mrg-conflicts">
                        {conflicts.map((c, i) => (
                            <li key={i} className="mrg-conflict">
                                <span className="mrg-conflict__type">{c.type || 'conflicto'}</span>
                                <span className="mrg-conflict__detail">{c.detail || CONFLICT_HELP[c.type] || 'Requiere resolución manual.'}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mrg-preview__foot">Resolvé los conflictos en el registro correspondiente y volvé a intentar. <strong>Nada se modificó.</strong></p>
                </div>
            ) : hardError ? (
                <div className="mrg-preview mrg-preview--error">
                    {previewError.status === 403 ? 'No tenés permiso para unificar este tipo de registro.'
                        : previewError.status === 404 ? 'Uno de los registros no existe o ya fue dado de baja.'
                        : (previewError.message || 'No se pudo previsualizar el impacto.')}
                </div>
            ) : (
                <div className="mrg-preview">
                    <div className="mrg-preview__head">
                        <span className="mrg-badge mrg-badge--ok">Sin conflictos</span>
                        <span className="mrg-preview__total">{affectedTotal} referencia(s) se repuntarían</span>
                    </div>
                    {affectedRows.length > 0 ? (
                        <ul className="mrg-bars">
                            {affectedRows.map(([k, n]) => (
                                <li key={k} className="mrg-bar">
                                    <span className="mrg-bar__label">{k}</span>
                                    <span className="mrg-bar__track"><span className="mrg-bar__fill" style={{ width: `${Math.round((Number(n) || 0) / maxAffected * 100)}%` }} /></span>
                                    <span className="mrg-bar__count">{n}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mrg-preview__foot">La absorbida no tiene referencias entrantes; solo se dará de baja.</p>
                    )}
                    {released.length > 0 && (
                        <p className="mrg-released">Libera valores únicos: {released.map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
                    )}
                    <div className="mrg-preview__actions">
                        <button type="button" className="mrg-btn mrg-btn--danger" onClick={() => setConfirming(true)} disabled={!canApply}>Unificar…</button>
                    </div>
                </div>
            )}

            {confirming && ready && (
                <MergeConfirmModal
                    entity={entity}
                    noun={cur.noun}
                    base={base}
                    absorbed={absorbed}
                    preview={preview}
                    labelOf={labelFor}
                    onClose={() => setConfirming(false)}
                    onConfirmed={reset}
                />
            )}
        </div>
    );
}
